import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Box, Skeleton } from '@mui/material';

import * as S from './styles';
import Avatars from 'components/Avatars';
import Button from 'components/Button';
import ButtonSidebar from 'components/ButtonSidebar';
import ConfirmLogoutModal from 'components/Logout/components/ConfirmLogoutModal';
import ModalSubMenu from 'components/ModalSubMenu';
import Spin from 'components/Spin';
import Text from 'components/Text';
import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useSideBar } from 'hooks/useSideBar';
import useUserImage from 'hooks/useUserImage';
import usePingOnline from 'microfrontends/hooks/usePingOnline';
import { MenuItem } from 'types/menuTypes';
import { formatUserName } from 'utils/formatUserName';
import { AvatarDefault, PoweredByAlgoricca } from 'utils/utilsIcons';

const SideBar = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const { open } = useSideBar();
  const { menu, isLoading, error } = useMenu();
  const [activeSubMenu, setActiveSubMenu] = useState<MenuItem | null>(null);
  const { userImage, loadingImage } = useUserImage(user?.id || '');
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const { selectMenuByDocumentId, currentLevel, secondLevelMenu, thirdLevelMenu } =
    useMenuSelection();
  const [isModalManuallyOpened, setIsModalManuallyOpened] = useState(false);

  usePingOnline();

  // Função segura para setar o activeSubMenu apenas para menus de primeiro nível
  const safeSetActiveSubMenu = useCallback(
    (menuItem: MenuItem | null) => {
      let menuToSet = menuItem;
      // Se não for null e não for de primeiro nível, busca o menu de primeiro nível correspondente
      if (menuItem && menuItem.level !== 'first') {
        const firstLevel = menu.find(item =>
          item.subMenu?.some(
            sub =>
              sub.label === menuItem?.label ||
              sub.subMenu?.some(sub2 => sub2.label === menuItem?.label)
          )
        );
        if (firstLevel) {
          menuToSet = firstLevel;
        }
      }
      setActiveSubMenu(menuToSet);
      // Se está definindo um menu, significa que foi aberto manualmente
      if (menuToSet) {
        setIsModalManuallyOpened(true);
      } else {
        setIsModalManuallyOpened(false);
      }
    },
    [menu]
  );

  // Restaura o ModalSubMenu baseado no currentLevel
  useEffect(() => {
    // Só sincroniza automaticamente se NÃO foi aberto manualmente
    if (!isModalManuallyOpened) {
      if (currentLevel === 'second' && secondLevelMenu && !activeSubMenu) {
        console.log('[SideBar] Restaurando ModalSubMenu para:', secondLevelMenu.label);
        safeSetActiveSubMenu(secondLevelMenu);
      } else if (currentLevel === 'third' && secondLevelMenu && !activeSubMenu) {
        console.log(
          '[SideBar] Restaurando ModalSubMenu para terceiro nível:',
          secondLevelMenu.label
        );
        safeSetActiveSubMenu(secondLevelMenu);
      } else if (currentLevel === 'first' && activeSubMenu && !secondLevelMenu) {
        // Só fecha se realmente voltou para primeiro nível E não tem secondLevelMenu
        console.log('[SideBar] Fechando ModalSubMenu - voltou para primeiro nível sem submenu');
        safeSetActiveSubMenu(null);
      } else if (
        activeSubMenu &&
        secondLevelMenu &&
        activeSubMenu.label !== secondLevelMenu.label
      ) {
        // Se o activeSubMenu não corresponde ao secondLevelMenu atual, atualiza
        console.log(
          '[SideBar] Atualizando ModalSubMenu - activeSubMenu não corresponde ao secondLevelMenu'
        );
        safeSetActiveSubMenu(secondLevelMenu);
      }
    } else {
      // Se foi aberto manualmente, verifica se ainda é válido manter aberto
      if (activeSubMenu && currentLevel === 'first' && !secondLevelMenu) {
        // Se voltou para primeiro nível e não tem secondLevelMenu, mantém o ModalSubMenu aberto
        console.log(
          '[SideBar] Mantendo ModalSubMenu aberto manualmente para:',
          activeSubMenu.label
        );
      } else if (
        activeSubMenu &&
        secondLevelMenu &&
        activeSubMenu.label !== secondLevelMenu.label
      ) {
        // Se o secondLevelMenu mudou mas o activeSubMenu ainda é válido, mantém
        console.log(
          '[SideBar] Mantendo ModalSubMenu aberto manualmente - secondLevelMenu mudou mas activeSubMenu ainda válido'
        );
      }
    }
  }, [currentLevel, secondLevelMenu, activeSubMenu, safeSetActiveSubMenu, isModalManuallyOpened]);

  // Determina se o ModalSubMenu deve estar aberto
  const shouldShowModalSubMenu = useMemo(() => {
    // Se temos um activeSubMenu, mostra (usuário clicou em menu com submenu)
    if (activeSubMenu) {
      console.log(
        '[SideBar] ModalSubMenu aberto por activeSubMenu:',
        activeSubMenu.label,
        'isModalManuallyOpened:',
        isModalManuallyOpened
      );
      return true;
    }

    // Se estamos em segundo ou terceiro nível e temos secondLevelMenu, mostra
    if ((currentLevel === 'second' || currentLevel === 'third') && secondLevelMenu) {
      console.log(
        '[SideBar] ModalSubMenu aberto por currentLevel:',
        currentLevel,
        'menu:',
        secondLevelMenu.label
      );
      return true;
    }

    // Se estamos no primeiro nível mas temos um secondLevelMenu válido, mostra
    if (currentLevel === 'first' && secondLevelMenu) {
      console.log(
        '[SideBar] ModalSubMenu aberto por first level com secondLevelMenu:',
        secondLevelMenu.label
      );
      return true;
    }

    console.log(
      '[SideBar] ModalSubMenu fechado - currentLevel:',
      currentLevel,
      'activeSubMenu:',
      !!activeSubMenu,
      'secondLevelMenu:',
      !!secondLevelMenu,
      'isModalManuallyOpened:',
      isModalManuallyOpened
    );
    return false;
  }, [activeSubMenu, currentLevel, secondLevelMenu, isModalManuallyOpened]);

  // Determina qual menu mostrar no ModalSubMenu
  const modalSubMenuData = useMemo(() => {
    // Se temos um activeSubMenu (usuário clicou em um menu), usa ele como base
    if (activeSubMenu) {
      console.log('[SideBar] ModalSubMenu usando activeSubMenu:', activeSubMenu.label);
      console.log('[SideBar][DEBUG] SubMenu do activeSubMenu:', {
        count: activeSubMenu.subMenu?.length || 0,
        menus:
          activeSubMenu.subMenu?.map(item => ({
            label: item.label,
            level: item.level,
            path: item.path,
          })) || [],
        fullMenus: activeSubMenu.subMenu || [],
      });
      return {
        menu: activeSubMenu,
        subMenu: activeSubMenu.subMenu || [],
        title: activeSubMenu.label,
      };
    }

    // Se estamos em segundo ou terceiro nível, encontra o menu pai correto
    if ((currentLevel === 'second' || currentLevel === 'third') && secondLevelMenu) {
      // Encontra o menu de primeiro nível que contém o secondLevelMenu
      const firstLevelMenu = menu.find(item =>
        item.subMenu?.some(sub => sub.label === secondLevelMenu.label)
      );

      if (firstLevelMenu) {
        console.log('[SideBar] ModalSubMenu usando menu pai encontrado:', firstLevelMenu.label);
        console.log('[SideBar][DEBUG] SubMenu do firstLevelMenu:', {
          count: firstLevelMenu.subMenu?.length || 0,
          menus:
            firstLevelMenu.subMenu?.map(item => ({
              label: item.label,
              level: item.level,
              path: item.path,
            })) || [],
          fullMenus: firstLevelMenu.subMenu || [],
        });
        return {
          menu: firstLevelMenu,
          subMenu: firstLevelMenu.subMenu || [],
          title: firstLevelMenu.label,
        };
      }
    }

    // Se estamos no primeiro nível mas temos secondLevelMenu, encontra o menu pai
    if (currentLevel === 'first' && secondLevelMenu) {
      const firstLevelMenu = menu.find(item =>
        item.subMenu?.some(sub => sub.label === secondLevelMenu.label)
      );

      if (firstLevelMenu) {
        console.log(
          '[SideBar] ModalSubMenu usando menu pai para first level:',
          firstLevelMenu.label
        );
        return {
          menu: firstLevelMenu,
          subMenu: firstLevelMenu.subMenu || [],
          title: firstLevelMenu.label,
        };
      }
    }

    console.log('[SideBar] Menu de primeiro nível não encontrado para ModalSubMenu');
    return null;
  }, [menu, activeSubMenu, currentLevel, secondLevelMenu, thirdLevelMenu]);

  const renderAvatar = useCallback(() => {
    if (loadingImage) {
      return <Skeleton variant="circular" width={56} height={56} />;
    }
    return (
      <Avatars
        img={userImage || AvatarDefault}
        size={{ width: open ? 56 : 32, height: open ? 56 : 32 }}
      />
    );
  }, [open, userImage, loadingImage]);

  const handleOpenLogoutModal = () => setLogoutModalOpen(true);
  const handleCloseLogoutModal = () => setLogoutModalOpen(false);

  const handleConfirmLogout = useCallback(() => {
    setLogoutModalOpen(false);
    setTimeout(() => {
      logout();
    }, 300);
  }, [logout]);

  const handleMenuClick = useCallback(
    (menuItem: MenuItem) => {
      console.log('[SideBar] handleMenuClick:', {
        label: menuItem.label,
        hasSubMenu: !!menuItem.subMenu?.length,
        hasFeature: !!menuItem.feature?.documentId,
        currentActiveSubMenu: activeSubMenu?.label,
        currentLevel,
        secondLevelMenu: secondLevelMenu?.label,
      });

      // Se o menu tem submenu e não tem feature.documentId, SEMPRE abre o ModalSubMenu
      if (menuItem.subMenu?.length && !menuItem.feature?.documentId) {
        console.log('[SideBar] Abrindo ModalSubMenu para menu com submenu:', menuItem.label);
        // SEMPRE define o activeSubMenu, mesmo se já estiver definido
        safeSetActiveSubMenu(menuItem);
        return;
      }

      // Se o menu tem feature.documentId, navega e fecha o ModalSubMenu
      if (menuItem.feature?.documentId) {
        console.log('[SideBar] Navegando para menu com feature:', menuItem.label);
        safeSetActiveSubMenu(null);
        selectMenuByDocumentId(menuItem.feature.documentId, true, undefined, menuItem.path);
        navigate(menuItem.path || '#');
        return;
      }

      // Se não tem submenu nem feature, apenas navega
      console.log('[SideBar] Navegando para menu simples:', menuItem.label);
      safeSetActiveSubMenu(null);
      navigate(menuItem.path || '#');
    },
    [
      activeSubMenu,
      safeSetActiveSubMenu,
      selectMenuByDocumentId,
      navigate,
      currentLevel,
      secondLevelMenu,
    ]
  );

  if (isLoading) return <Spin />;

  if (error)
    return (
      <Text size="small" color="red" bold textAlign="center">
        Erro ao carregar o menu
      </Text>
    );

  return (
    <S.Container open={open}>
      <div className="avatar">
        {renderAvatar()}
        {open && (
          <div style={{ marginTop: '0.5rem' }}>
            <Text size="small" bold className="user-name">
              Bem vindo,
            </Text>
            <Text size="small" bold className="user-name">
              {formatUserName(`${user?.first_name} ${user?.last_name}`)}
            </Text>
          </div>
        )}
      </div>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <S.WrapperMenu>
          {menu.map(menuItem => (
            <ButtonSidebar
              label={menuItem.label || ''}
              path={menuItem.path || '#'}
              icon={menuItem.icon}
              isActive={menuItem.isActive}
              subMenu={menuItem.subMenu || []}
              key={menuItem.label}
              disabled={!menuItem.isActive}
              onClick={() => handleMenuClick(menuItem)}
              level="first"
            />
          ))}
        </S.WrapperMenu>
      </Box>

      {shouldShowModalSubMenu && (
        <ModalSubMenu
          subMenu={modalSubMenuData?.subMenu || []}
          title={modalSubMenuData?.title || ''}
          closeSubMenu={() => {
            // Só fecha se estivermos no primeiro nível E não tivermos secondLevelMenu
            if (currentLevel === 'first' && !secondLevelMenu) {
              console.log('[SideBar] Fechando ModalSubMenu manualmente');
              safeSetActiveSubMenu(null);
              setIsModalManuallyOpened(false);
            } else {
              console.log(
                '[SideBar] Tentativa de fechar ModalSubMenu ignorada - currentLevel:',
                currentLevel,
                'hasSecondLevelMenu:',
                !!secondLevelMenu
              );
            }
          }}
          open={shouldShowModalSubMenu}
          isSubMenuActive={!!modalSubMenuData?.subMenu?.find(menu => menu.isActive)}
          level={currentLevel === 'second' ? 'second' : 'third'}
        />
      )}

      <S.WrapperFooter>
        {open && (
          <>
            <S.Logo>
              <Text textAlign="left" size="xSmall" color="white">
                Powered By
              </Text>
              <img src={PoweredByAlgoricca} alt="Powered By Algoricca" />
            </S.Logo>

            <div className="plan">
              {user?.user_to_organization && user?.user_to_organization.length > 0 ? (
                user?.user_to_organization.map(org => (
                  <Text size="xSmall" key={org.organization.uuid}>
                    {org?.individual_plan?.name?.includes('Plano') ? (
                      <b>{org?.individual_plan?.name || 'N/A'}</b>
                    ) : (
                      <b>{`Plano ${org?.individual_plan?.name || 'N/A'}`}</b>
                    )}
                  </Text>
                ))
              ) : (
                <Text size="xSmall">Nenhum plano associado</Text>
              )}
            </div>
          </>
        )}

        <Button onClick={handleOpenLogoutModal} variant="minimal" size="small">
          Sair
        </Button>
      </S.WrapperFooter>

      <ConfirmLogoutModal
        open={logoutModalOpen}
        onClose={handleCloseLogoutModal}
        onConfirm={handleConfirmLogout}
      />
    </S.Container>
  );
};

export default SideBar;
